import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export type SiteSettings = Record<string, string>;

let cachedSettings: SiteSettings | null = null;
const SETTINGS_UPDATED_EVENT = 'site-settings-updated';

/** Call this after saving to push new values to all useSiteSettings consumers instantly */
export function broadcastSettingsUpdate(updated: SiteSettings) {
  cachedSettings = { ...(cachedSettings ?? {}), ...updated };
  window.dispatchEvent(new CustomEvent(SETTINGS_UPDATED_EVENT, { detail: cachedSettings }));
}

export function useSiteSettings() {
  const [settings, setSettings] = useState<SiteSettings>(cachedSettings ?? {});
  const [loading, setLoading] = useState(!cachedSettings);

  const fetchSettings = useCallback(async () => {
    const { data } = await supabase.from('site_settings').select('key, value');
    const map: SiteSettings = {};
    (data ?? []).forEach((r: { key: string; value: string }) => { map[r.key] = r.value ?? ''; });
    cachedSettings = map;
    setSettings(map);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (cachedSettings) {
      setSettings({ ...cachedSettings });
      setLoading(false);
    } else {
      // Either useDynamicSEO will broadcast first (and the handler below catches it),
      // or we fall back to fetching ourselves.
      fetchSettings();
    }

    // Listen for live updates — covers both: initial seed from useDynamicSEO on mount,
    // and subsequent saves from the admin branding panel.
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SiteSettings>).detail;
      setSettings({ ...detail });
      setLoading(false);
    };
    window.addEventListener(SETTINGS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SETTINGS_UPDATED_EVENT, handler);
  }, [fetchSettings]);

  return { settings, loading };
}
